/**
 * The editable display of one slot — the body of the expanded edit card, one
 * component per slot type, plus the small controls those bodies share.
 *
 * Editing is tap-to-expand / commit-on-collapse: every keystroke writes through
 * to the slot immediately, so "commit" is really just leaving edit mode. There
 * is no per-card Cancel — the ✓ in the header, tapping another card, or the
 * screen-level Done all commit the same way. The card these bodies sit inside is
 * `SlotEditCard`; `SlotViews` is what pairs a body with its type.
 */

import React, { useEffect, useRef, useState } from 'react';
import { IonIcon, IonPopover } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import {
  FREE_TEXT_MAX_LENGTH_TIERS,
  FREE_TEXT_MAX_LENGTH_HINTS,
  FREE_TEXT_MAX_LENGTH_DEFAULT,
} from '@/modules/config/constants';
import type {
  FillInSlot,
  GoalSlot,
  OptionalSlot,
  SelectSlot,
  TextSlot,
} from './schema';
import type { AddressingModeGate } from '@/modules/share';
import type { MessageIntent, SharingModeGate } from '@/modules/template/types';
import { SlotLogic } from './slot_logic';
import type { FormBuilderContext, VariableSlot } from './slot_logic';
import type { SharingFormSlot } from './types';
import {
  InvisibleCharShowingField,
  InvisibleCharShowingLine,
} from './InvisibleCharShower';
import styles from './slots.module.css';

/** The slot types that share one plain-text editor. */
type TextEditorSlot = TextSlot | GoalSlot;

export interface SlotEditorProps<S extends SharingFormSlot = SharingFormSlot> {
  slot: S;
  testId: string;
  /** What the builder knows about the topic on screen — see its own docs. */
  context: FormBuilderContext;
  onChange: (next: SharingFormSlot) => void;
  onCommit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  /**
   * The soft-link controls, when the builder offers linking: the forms a copy
   * can be linked into, the ones the slot is already linked with, and the
   * action that links it.
   */
  link?: SlotLinkProps;
  /**
   * The editor's own text field, when it has one. The card's footer sits
   * outside the body editor but its Insert menu writes into that field at the
   * caret, so the two need to reach the same element.
   */
  fieldRef?: React.RefObject<HTMLTextAreaElement | null>;
}

/** One intent's form, as the link popup lists it. */
export interface SlotLinkTarget {
  /** The intent whose form it is: what the popup hands back when ticked. */
  readonly intent: MessageIntent;
  /** How the author knows the form: its intent, e.g. "🔔 Reminder". */
  readonly label: string;
}

/** What a card needs to show and change a slot's soft links. */
export interface SlotLinkProps {
  /** Every intent's form, in config order — what the popup lists. */
  readonly targets: readonly SlotLinkTarget[];
  /** The intent whose form holds this slot, listed ticked. */
  readonly home: MessageIntent;
  /** The other forms already holding a slot linked to this one. */
  readonly linkedWith: readonly SlotLinkTarget[];
  /** Link the slot with exactly these intents, unlinking the rest. */
  readonly onSetLinks: (intents: readonly MessageIntent[]) => void;
}

/**
 * A small menu hung off a control.
 *
 * `IonPopover` is the platform's own anchored popup — it handles placement,
 * the backdrop, dismissal and the escape hatches, which a hand-rolled panel has
 * to keep re-earning as entries are added. Opened from the click event, the way
 * the app's other popover menu does.
 *
 * `onOpen` fires before focus moves into the popover, which is the last moment
 * a caller can read something from the field the menu belongs to — a caret, for
 * instance, which opening the menu is about to take away.
 */
const SlotMenu: React.FC<{
  label: React.ReactNode;
  triggerClassName: string;
  testId: string;
  onOpen?: () => void;
  /** The trigger button, for a caller that opens the menu itself. */
  triggerRef?: React.Ref<HTMLButtonElement>;
  children: (close: () => void) => React.ReactNode;
}> = ({ label, triggerClassName, testId, onOpen, triggerRef, children }) => {
  const [event, setEvent] = useState<MouseEvent | undefined>();
  const close = () => setEvent(undefined);
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        // Pressing this must not move focus out of whatever the menu belongs
        // to, or `onOpen` would already be too late to read a caret from it.
        onMouseDown={e => e.preventDefault()}
        onClick={e => {
          onOpen?.();
          setEvent(e.nativeEvent);
        }}
        data-testid={testId}
      >
        {label}
      </button>
      <IonPopover
        isOpen={event !== undefined}
        event={event}
        onDidDismiss={close}
        side="top"
        alignment="start"
      >
        <div className={styles.menu} data-testid={`${testId}-popover`}>
          {children(close)}
        </div>
      </IonPopover>
    </>
  );
};

/**
 * The max-length picker: fixed tiers, current value checked, choose-and-dismiss.
 * The chip itself always shows the live limit, so it doubles as the indicator
 * and no extra row is needed on the collapsed card.
 */
const MaxLengthPicker: React.FC<{
  value: number;
  testId: string;
  onChange: (value: number) => void;
}> = ({ value, testId, onChange }) => (
  <SlotMenu
    label={`${value} ▾`}
    triggerClassName={styles.valueChip}
    testId={testId}
  >
    {close =>
      FREE_TEXT_MAX_LENGTH_TIERS.map(tier => (
        <button
          key={tier}
          type="button"
          onClick={() => {
            onChange(tier);
            close();
          }}
          data-testid={`${testId}-tier-${tier}`}
          className={styles.tierOption}
        >
          <span className={styles.tierCheck}>{tier === value ? '✓' : ''}</span>
          <span className={styles.tierLabel}>{tier}</span>
          <span className={styles.tierHint}>
            {FREE_TEXT_MAX_LENGTH_HINTS[tier]}
          </span>
        </button>
      ))
    }
  </SlotMenu>
);

/** One position of a `SegmentedPicker`. */
interface SegmentedChoice<T extends string> {
  readonly value: T;
  readonly label: string;
  /** Suffix of the option's test id, under the picker's own. */
  readonly testId: string;
}

/**
 * A setting whose answer is one of a few named states, as a slider the thumb
 * slides along.
 *
 * The thumb's position carries the answer as well as the weight does, so the
 * live option is legible without reading the labels — the choice is a state,
 * and it sits in the setting row where the other per-slot settings do. Which
 * position is live and how many there are are runtime values, so they ride in
 * as custom properties and the stylesheet derives the thumb from them.
 */
function SegmentedPicker<T extends string>({
  choices,
  value,
  labelledBy,
  testId,
  onChange,
}: {
  choices: readonly SegmentedChoice<T>[];
  value: T;
  labelledBy: string;
  testId: string;
  onChange: (value: T) => void;
}) {
  const index = Math.max(
    0,
    choices.findIndex(choice => choice.value === value)
  );
  return (
    <div
      role="radiogroup"
      aria-labelledby={labelledBy}
      className={styles.segmented}
      style={
        {
          '--seg-index': index,
          '--seg-count': choices.length,
        } as React.CSSProperties
      }
      data-testid={testId}
    >
      <span className={styles.segmentedThumb} aria-hidden="true" />
      {choices.map(choice => (
        <button
          key={choice.testId}
          type="button"
          role="radio"
          aria-checked={choice.value === value}
          className={styles.segmentedOption}
          onClick={() => onChange(choice.value)}
          data-testid={`${testId}-${choice.testId}`}
        >
          {choice.label}
        </button>
      ))}
    </div>
  );
}

// Ordered as they sit in the slider: the restrictive positions first, so the
// thumb resting left means "kept out of some messages".
const ADDRESSING_MODE_CHOICES: readonly SegmentedChoice<AddressingModeGate>[] =
  [
    { value: 'dm', label: 'DMs only', testId: 'dm' },
    { value: 'group-text', label: 'Group only', testId: 'group' },
    { value: 'all', label: 'All msgs', testId: 'all' },
  ];

const SHARING_MODE_CHOICES: readonly SegmentedChoice<SharingModeGate>[] = [
  { value: 'fresh', label: 'First share', testId: 'fresh' },
  { value: 'reshare', label: 'Reshare', testId: 'reshare' },
  { value: 'all', label: 'Both', testId: 'all' },
];

/**
 * Which sharing modes a slot is offered in. Every slot type can be kept out
 * this way, so this row is drawn by the shared editor chrome rather than by
 * each type's own editor.
 */
export const SharingModesRow: React.FC<{
  slot: SharingFormSlot;
  testId: string;
  onChange: (next: SharingFormSlot) => void;
}> = ({ slot, testId, onChange }) => (
  <div className={styles.settingRow}>
    <span id={`${testId}-sharing-label`}>Offer in</span>
    <SegmentedPicker
      choices={SHARING_MODE_CHOICES}
      value={slot.forSharingModes ?? 'all'}
      labelledBy={`${testId}-sharing-label`}
      testId={`${testId}-sharing`}
      onChange={gate => onChange(SlotLogic.setSharingModes(slot, gate))}
    />
  </div>
);

/**
 * The variable a slot assigns: the known names to pick from, plus a write-in
 * for a new one.
 *
 * The known names are every variable the setup already assigns (see
 * `FormBuilderContext.variableNames`), so a variable set on one slot is
 * offered on the next. Picking "New variable…" puts a field in place of the
 * chip, and every keystroke there writes through to the slot like the rest of
 * the card, which is also what adds the new name to the list the other slots
 * see.
 *
 * A slot that has just been asked to assign a variable has none named yet, so
 * the picker opens itself — unless nothing is known yet, in which case the
 * field for a new name is all there is to offer.
 */
const VariablePicker: React.FC<{
  value: string;
  known: readonly string[];
  testId: string;
  onChange: (name: string) => void;
}> = ({ value, known, testId, onChange }) => {
  const [writingNew, setWritingNew] = useState(
    () => known.length === 0 || (value !== '' && !known.includes(value))
  );
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const newNameRef = useRef<HTMLInputElement | null>(null);
  // Choosing "New variable…" is a request to type one, so the field takes
  // focus then — but not when the card merely opens on a name being written.
  const focusNewName = useRef(false);
  useEffect(() => {
    if (!writingNew || !focusNewName.current) return;
    focusNewName.current = false;
    newNameRef.current?.focus();
  }, [writingNew]);
  const opensItself = useRef(value === '' && known.length > 0);
  useEffect(() => {
    if (!opensItself.current) return;
    opensItself.current = false;
    triggerRef.current?.click();
  }, []);

  if (writingNew) {
    return (
      <input
        ref={newNameRef}
        value={value}
        onChange={e => onChange(e.target.value.trim())}
        placeholder="variableName"
        aria-label="Variable name"
        autoComplete="off"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        data-testid={`${testId}-new`}
        className={styles.variableInput}
      />
    );
  }
  // The name being typed is already in `known`, so it is not offered twice.
  return (
    <SlotMenu
      label={value === '' ? 'Choose ▾' : `${value} ▾`}
      triggerClassName={styles.valueChip}
      triggerRef={triggerRef}
      testId={testId}
    >
      {close => (
        <>
          {known.map(name => (
            <button
              key={name}
              type="button"
              onClick={() => {
                onChange(name);
                close();
              }}
              data-testid={`${testId}-option-${name}`}
              className={styles.tierOption}
            >
              <span className={styles.tierCheck}>
                {name === value ? '✓' : ''}
              </span>
              <span className={`${styles.tierLabel} ${styles.variableName}`}>
                {name}
              </span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              onChange('');
              focusNewName.current = true;
              setWritingNew(true);
              close();
            }}
            data-testid={`${testId}-option-new`}
            className={styles.tierOption}
          >
            <span className={styles.tierCheck} />
            <span className={styles.tierLabel}>New variable…</span>
          </button>
        </>
      )}
    </SlotMenu>
  );
};

/**
 * The two variable options a Select, Fill in or Text slot offers: whether it
 * prints into the message, and whether its printed answer is assigned to a
 * variable. Both are shared by those three types, so the editor chrome draws
 * them once, under the sharing-mode row.
 */
export const VariableRows: React.FC<{
  slot: VariableSlot;
  testId: string;
  context: FormBuilderContext;
  onChange: (next: SharingFormSlot) => void;
}> = ({ slot, testId, context, onChange }) => {
  const variable = SlotLogic.declaredVariableName(slot);
  return (
    <>
      <label className={styles.settingRow}>
        <span>Include in message</span>
        <input
          type="checkbox"
          checked={SlotLogic.emitsInMessage(slot)}
          onChange={e =>
            onChange(SlotLogic.setEmitsInMessage(slot, e.target.checked))
          }
          className={styles.checkbox}
          data-testid={`${testId}-include`}
        />
      </label>
      <label className={styles.settingRow}>
        <span>Assign to variable</span>
        <input
          type="checkbox"
          checked={variable !== null}
          onChange={e =>
            onChange(SlotLogic.setDefine(slot, e.target.checked ? '' : null))
          }
          className={styles.checkbox}
          data-testid={`${testId}-assign`}
        />
      </label>
      {variable !== null && (
        <div className={styles.nestedSettingRow}>
          <span>↳ Variable</span>
          <VariablePicker
            value={variable}
            known={context.variableNames}
            testId={`${testId}-variable`}
            onChange={name => onChange(SlotLogic.setDefine(slot, name))}
          />
        </div>
      )}
    </>
  );
};

/**
 * Literal text. Leading and trailing spaces are load-bearing here — they are
 * what separates the words once the body is composed — so the field is
 * monospaced, never trimmed, and draws every space as a dot.
 */
export const TextSlotEditor: React.FC<
  SlotEditorProps<TextEditorSlot>
> = props => {
  const text = props.slot.expr;
  return (
    <InvisibleCharShowingField value={text} className={styles.exprInput}>
      <textarea
        ref={props.fieldRef}
        value={text}
        onChange={e => props.onChange({ ...props.slot, expr: e.target.value })}
        spellCheck={false}
        autoCorrect="off"
        data-testid={`${props.testId}-text`}
        className={`${styles.exprInput} ${styles.invisibleCharFieldInput}`}
      />
    </InvisibleCharShowingField>
  );
};

/** A goal slot: the text editor plus a reminder of what the slot is tied to. */
export const GoalSlotEditor: React.FC<SlotEditorProps<GoalSlot>> = props => (
  <>
    <TextSlotEditor {...props} />
    <div className={styles.editorHelp}>
      Tied to the topic&apos;s impression goal. Reference primitives like{' '}
      {'{_runImpressionPeople}'} and {'{_impressionGoal}'} to keep the numbers
      live.
    </div>
  </>
);

/**
 * Optional text: an `expr` the sender opts into as they compose. Curly-brace
 * references to
 * primitives expand at send time, and leading/trailing spaces are kept —
 * they are what separate the text from the words around it.
 */
export const OptionalSlotEditor: React.FC<
  SlotEditorProps<OptionalSlot>
> = props => {
  const slot = props.slot;
  return (
    <>
      <InvisibleCharShowingLine
        value={slot.expr}
        onChange={expr => props.onChange({ ...slot, expr })}
        placeholder=" optional text"
        aria-label="Optional text"
        spellCheck={false}
        autoCorrect="off"
        data-testid={`${props.testId}-text`}
        className={`${styles.titleField} ${styles.optionalTitleField}`}
      />
      <div className={styles.editorHelp}>
        Optionally included by the sender. Use {'{_primitiveName}'} to insert a
        value, and keep any spacing you need around it.
      </div>
      <div className={styles.settingRow}>
        <span id={`${props.testId}-addressing-label`}>Sent as</span>
        <SegmentedPicker
          choices={ADDRESSING_MODE_CHOICES}
          value={slot.forAddressingModes ?? 'all'}
          labelledBy={`${props.testId}-addressing-label`}
          testId={`${props.testId}-addressing`}
          onChange={gate =>
            props.onChange(SlotLogic.setAddressingModes(slot, gate))
          }
        />
      </div>
    </>
  );
};

/** Multiple choice: preset options, plus the optional "Other" write-in. */
export const SelectSlotEditor: React.FC<
  SlotEditorProps<SelectSlot>
> = props => {
  const slot = props.slot;
  const presets = SlotLogic.presetOptions(slot);
  const writeIn = SlotLogic.writeInOf(slot);

  return (
    <>
      <input
        value={slot.prompt}
        onChange={e => props.onChange({ ...slot, prompt: e.target.value })}
        placeholder="Prompt (required)"
        aria-label="Prompt"
        spellCheck={false}
        autoCorrect="off"
        data-testid={`${props.testId}-prompt`}
        className={styles.titleField}
      />

      {presets.map((option, i) => (
        <div key={i} className={styles.optionRow}>
          <span className={styles.radioGlyph} aria-hidden="true" />
          <InvisibleCharShowingLine
            value={option}
            onChange={value =>
              props.onChange(SlotLogic.setPresetOption(slot, i, value))
            }
            aria-label={`Option ${i + 1}`}
            spellCheck={false}
            autoCorrect="off"
            data-testid={`${props.testId}-option-${i}`}
            className={styles.optionInput}
          />
          <button
            type="button"
            className={styles.iconButton}
            onClick={() =>
              props.onChange(SlotLogic.removePresetOption(slot, i))
            }
            aria-label={`Remove option ${i + 1}`}
            data-testid={`${props.testId}-option-${i}-remove`}
          >
            <IonIcon icon={closeOutline} className={styles.removeIcon} />
          </button>
          <button
            type="button"
            className={`${styles.iconButton} ${styles.moveIcon}`}
            onClick={() =>
              props.onChange(SlotLogic.movePresetOption(slot, i, i - 1))
            }
            disabled={i === 0}
            aria-label={`Move option ${i + 1} up`}
            data-testid={`${props.testId}-option-${i}-up`}
          >
            ▲
          </button>
          <button
            type="button"
            className={`${styles.iconButton} ${styles.moveIcon}`}
            onClick={() =>
              props.onChange(SlotLogic.movePresetOption(slot, i, i + 1))
            }
            disabled={i === presets.length - 1}
            aria-label={`Move option ${i + 1} down`}
            data-testid={`${props.testId}-option-${i}-down`}
          >
            ▼
          </button>
        </div>
      ))}

      <button
        type="button"
        className={styles.addOption}
        onClick={() => props.onChange(SlotLogic.addPresetOption(slot))}
        data-testid={`${props.testId}-add-option`}
      >
        ＋ Add option
      </button>

      <div className={styles.settingRow}>
        <span id={`${props.testId}-write-in-label`}>
          Allow “Other” write-in
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={writeIn !== null}
          aria-labelledby={`${props.testId}-write-in-label`}
          className={`${styles.toggle} ${writeIn !== null ? styles.toggleOn : ''}`}
          onClick={() =>
            props.onChange(SlotLogic.setWriteInEnabled(slot, writeIn === null))
          }
          data-testid={`${props.testId}-write-in`}
        >
          <span className={styles.toggleKnob} />
        </button>
      </div>

      {writeIn && (
        <div className={styles.nestedSettingRow}>
          <span>↳ Placeholder</span>
          <input
            value={writeIn.placeholder ?? ''}
            onChange={e =>
              props.onChange(
                SlotLogic.setWriteInPlaceholder(slot, e.target.value)
              )
            }
            placeholder="Hint shown in the empty field (optional)"
            aria-label="Write-in placeholder"
            spellCheck={false}
            autoCorrect="off"
            data-testid={`${props.testId}-write-in-placeholder`}
            className={`${styles.plainInput} ${styles.nestedInput}`}
          />
        </div>
      )}
      {writeIn && (
        <div className={styles.nestedSettingRow}>
          <span>↳ Max length</span>
          <MaxLengthPicker
            value={writeIn.maxlength ?? FREE_TEXT_MAX_LENGTH_DEFAULT}
            testId={`${props.testId}-write-in-max`}
            onChange={maxlength =>
              props.onChange(SlotLogic.setWriteInMaxLength(slot, maxlength))
            }
          />
        </div>
      )}
    </>
  );
};

/** A standalone free-text field: the recipient types the whole answer. */
export const FillInSlotEditor: React.FC<
  SlotEditorProps<FillInSlot>
> = props => {
  const slot = props.slot;
  return (
    <>
      <input
        value={slot.prompt}
        onChange={e => props.onChange({ ...slot, prompt: e.target.value })}
        placeholder="Prompt (required)"
        aria-label="Prompt"
        spellCheck={false}
        autoCorrect="off"
        data-testid={`${props.testId}-prompt`}
        className={styles.titleField}
      />
      <input
        value={slot.placeholder ?? ''}
        onChange={e => props.onChange({ ...slot, placeholder: e.target.value })}
        placeholder="Hint shown in the empty field (optional)"
        aria-label="Placeholder"
        spellCheck={false}
        autoCorrect="off"
        data-testid={`${props.testId}-placeholder`}
        className={styles.plainInput}
      />
      <div className={styles.settingRow}>
        <span>Max length</span>
        <MaxLengthPicker
          value={slot.maxlength ?? FREE_TEXT_MAX_LENGTH_DEFAULT}
          testId={`${props.testId}-max`}
          onChange={maxlength => props.onChange({ ...slot, maxlength })}
        />
      </div>
    </>
  );
};
